<script setup lang="ts">
import changelogSource from "../../CHANGELOG.md?raw";

definePageMeta({
  layout: "simple",
});

const localePath = useLocalePath();

type ChangelogEntry = {
  version?: string;
  date: string;
  technicalTitle: string;
  userSummary?: string;
  section: string;
  notes: string[];
};

type ChangelogSection = {
  label: string;
  entries: ChangelogEntry[];
};

type ChangelogDaySection = {
  label: string;
  entries: ChangelogEntry[];
};

type ChangelogDay = {
  date: string;
  version?: string;
  sections: ChangelogDaySection[];
  totalEntries: number;
};

type ChangelogMonth = {
  label: string;
  sections: ChangelogSection[];
};

type ChangelogMonthView = ChangelogMonth & {
  totalEntries: number;
  days: ChangelogDay[];
};

function isNoiseTitle(title: string) {
  const normalized = title
    .trim()
    .toLowerCase()
    .replace(/^v\d+\.\d+\.\d+\s*-\s*/, "");
  return (
    normalized.startsWith("merge:") ||
    normalized.startsWith("merge ") ||
    normalized.startsWith("merge remote-tracking branch") ||
    normalized.startsWith("sync development") ||
    normalized.startsWith("update changelog") ||
    normalized.startsWith("update changelog.md") ||
    normalized.startsWith("group changelog entries by month") ||
    normalized.startsWith("add automatic codex changelog entries") ||
    normalized.startsWith("normalize commit version bump to semver") ||
    normalized.startsWith("skip hook setup when scripts are unavailable")
  );
}

function buildChangelog(source: string): ChangelogMonth[] {
  const months = new Map<string, ChangelogMonth>();
  let currentMonth: ChangelogMonth | null = null;
  let currentDate = "";
  let currentEntry: ChangelogEntry | null = null;

  const ensureMonth = (label: string) => {
    if (!months.has(label)) {
      months.set(label, { label, sections: [] });
    }

    currentMonth = months.get(label) ?? null;
  };

  for (const rawLine of source.split(/\r?\n/)) {
    const line = rawLine.trim();

    if (!line || line.startsWith("<!--")) {
      continue;
    }

    if (line.toLowerCase() === "# changelog") {
      currentMonth = null;
      currentDate = "";
      currentEntry = null;
      continue;
    }

    const monthMatch = line.match(/^##\s+(.+)$/);
    if (monthMatch) {
      ensureMonth(monthMatch[1].trim());
      currentDate = "";
      currentEntry = null;
      continue;
    }

    const dayMatch = line.match(/^###\s+(\d{4}-\d{2}-\d{2})$/);
    if (dayMatch) {
      currentDate = dayMatch[1];
      currentEntry = null;
      continue;
    }

    const titleMatch = line.match(/^####\s+(.+)$/);
    if (titleMatch && currentMonth) {
      const title = titleMatch[1].trim();
      const versionMatch = title.match(/^v?(\d+\.\d+\.\d+)\s*-\s*(.+)$/);

      if (isNoiseTitle(title)) {
        currentEntry = null;
        continue;
      }

      currentEntry = {
        date: currentDate,
        technicalTitle: title,
        section: "",
        notes: [],
        version: versionMatch?.[1],
      };
      continue;
    }

    const sectionMatch = line.match(/^#####\s+(.+)$/);
    if (sectionMatch && currentEntry) {
      currentEntry.section = sectionMatch[1].trim();
      if (!currentMonth) continue;

      const sectionLabel = currentEntry.section || "Updates";
      let section = currentMonth.sections.find((item) => item.label === sectionLabel);
      if (!section) {
        section = { label: sectionLabel, entries: [] };
        currentMonth.sections.push(section);
      }

      section.entries.push(currentEntry);
      continue;
    }

    const bulletMatch = line.match(/^-+\s+(.+)$/);
    if (bulletMatch && currentEntry) {
      const bullet = bulletMatch[1].trim();
      const userMatch = bullet.match(/^User:\s*(.+)$/i);
      if (userMatch) {
        currentEntry.userSummary = userMatch[1].trim();
      } else {
        currentEntry.notes.push(bullet);
      }
    }
  }

  return Array.from(months.values()).map((month) => ({
    ...month,
  }));
}

function formatTimelineDate(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00Z`));
}

function parseVersion(value?: string) {
  if (!value) return null;

  const match = value.match(/^(\d+)\.(\d+)\.(\d+)$/);
  if (!match) return null;

  return match.slice(1).map(Number) as [number, number, number];
}

function compareVersions(left?: string, right?: string) {
  const parsedLeft = parseVersion(left);
  const parsedRight = parseVersion(right);

  if (!parsedLeft && !parsedRight) return 0;
  if (!parsedLeft) return -1;
  if (!parsedRight) return 1;

  for (let index = 0; index < parsedLeft.length; index += 1) {
    if (parsedLeft[index] !== parsedRight[index]) {
      return parsedLeft[index] - parsedRight[index];
    }
  }

  return 0;
}

function getDayAnchorId(value: string) {
  return `changelog-${value}`;
}

function buildMonthDays(month: ChangelogMonth): ChangelogDay[] {
  const dayMap = new Map<string, ChangelogDay>();
  const orderedDates: string[] = [];

  for (const section of month.sections) {
    for (const entry of section.entries) {
      let day = dayMap.get(entry.date);
      if (!day) {
        day = {
          date: entry.date,
          version: entry.version,
          sections: [],
          totalEntries: 0,
        };
        dayMap.set(entry.date, day);
        orderedDates.push(entry.date);
      } else if (compareVersions(entry.version, day.version) > 0) {
        day.version = entry.version;
      }

      let daySection = day.sections.find((item) => item.label === section.label);
      if (!daySection) {
        daySection = { label: section.label, entries: [] };
        day.sections.push(daySection);
      }

      daySection.entries.push(entry);
      day.totalEntries += 1;
    }
  }

  return orderedDates
    .sort((left, right) => right.localeCompare(left))
    .map((date) => dayMap.get(date) as ChangelogDay);
}

const changelog = buildChangelog(changelogSource);
const months: ChangelogMonthView[] = changelog.map((month) => {
  const days = buildMonthDays(month);

  return {
    ...month,
    days,
    totalEntries: month.sections.reduce(
      (sum, section) => sum + section.entries.length,
      0
    ),
  };
});

const totalEntries = months.reduce(
  (sum, month) => sum + month.sections.reduce((sectionSum, section) => sectionSum + section.entries.length, 0),
  0
);
const latestMonth = months[0]?.label ?? "";
const latestPublishedDay = months[0]?.days[0]?.date ?? "";

function goBack() {
  if (process.client && window.history.length > 1) {
    window.history.back();
    return;
  }

  navigateTo(localePath("/"));
}

useSeoMeta({
  title: "Changelog",
  description: "Customer-visible release notes grouped by month from the repository changelog.",
});
</script>

<template>
  <div class="relative min-h-screen overflow-hidden bg-[#07090d] text-neutral-100">
    <div
      class="pointer-events-none absolute inset-0 overflow-hidden"
      aria-hidden="true">
      <div
        class="absolute -top-32 left-1/2 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-primary-500/20 blur-3xl" />
      <div
        class="absolute top-[24rem] right-[-8rem] h-[24rem] w-[24rem] rounded-full bg-white/5 blur-3xl" />
      <div
        class="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-black/40 to-transparent" />
    </div>

    <header class="sticky top-0 z-20 border-b border-white/10 bg-black/60 backdrop-blur-xl">
      <div
        class="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-6 py-4">
        <button
          type="button"
          class="text-xs uppercase tracking-[0.35em] text-neutral-300 transition hover:text-primary-300"
          @click="goBack">
          Back to GFAL Marketplace
        </button>
      </div>
    </header>

    <main class="relative">
      <section
        class="mx-auto grid max-w-7xl items-start gap-10 px-6 pb-10 pt-5 lg:grid-cols-[1.15fr_0.85fr] lg:pb-12 lg:pt-6">
        <div class="space-y-4 animate-[changelog-rise_700ms_ease_both]">
          <p class="text-xs uppercase tracking-[0.5em] text-primary-300">
            Changelog
          </p>
          <h1 class="max-w-3xl text-5xl font-semibold tracking-tight text-balance text-white sm:text-6xl lg:text-7xl">
            What we've been working on
          </h1>
          <p class="max-w-2xl text-base leading-7 text-neutral-300 sm:text-lg">
            Latest updates to the GFAL Marketplace.
          </p>
          <div class="flex flex-wrap gap-3 text-xs uppercase tracking-[0.3em] text-neutral-400">
            <span class="rounded-full border border-white/10 bg-white/5 px-3 py-2">
              {{ changelog.length }} months
            </span>
            <span class="rounded-full border border-white/10 bg-white/5 px-3 py-2">
              {{ totalEntries }} updates
            </span>
          </div>
        </div>

        <aside class="lg:justify-self-end">
          <div class="border border-white/10 bg-white/5 p-6 backdrop-blur-md">
            <p class="text-xs uppercase tracking-[0.35em] text-neutral-400">
              Latest month
            </p>
            <p class="mt-4 text-3xl font-semibold tracking-tight text-white">
              {{ latestMonth }}
            </p>
            <p class="mt-3 text-sm uppercase tracking-[0.35em] text-primary-300">
              {{ months[0]?.days.length ?? 0 }} days with updates
            </p>
            <p class="mt-4 text-base leading-7 text-neutral-200">
              {{ months[0]?.totalEntries ?? 0 }} updates in the latest month.
            </p>
            <p class="mt-8 text-sm leading-6 text-neutral-400">
              Scroll down for the monthly archive. The structure is intentionally
              stripped down so customers can scan the release history by type,
              without extra sub-groups or commit noise.
            </p>
          </div>
        </aside>
      </section>

      <section class="mx-auto max-w-7xl px-6 pb-24">
        <div v-for="(month, monthIndex) in months" :key="month.label" class="py-10">
          <div class="grid gap-8 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <div
              class="lg:sticky lg:top-28 h-fit animate-[changelog-rise_700ms_ease_both]"
              :style="{ animationDelay: `${monthIndex * 120}ms` }">
              <p class="text-xs uppercase tracking-[0.45em] text-primary-300">
                Month
              </p>
              <h2 class="mt-3 text-3xl font-semibold tracking-tight text-white">
                {{ month.label }}
              </h2>
              <p class="mt-3 text-sm leading-6 text-neutral-400">
                {{ month.totalEntries }}
                update{{
                  month.totalEntries === 1
                    ? ""
                    : "s"
                }}
              </p>
              <p class="mt-8 text-xs uppercase tracking-[0.45em] text-neutral-500">
                Days
              </p>
              <div class="relative mt-5 pl-7">
                <div
                  class="absolute left-[10px] top-2 bottom-2 w-[2px] bg-gradient-to-b from-primary-300 via-white/30 to-white/5" />
                <div
                  v-for="(day, dayIndex) in month.days"
                  :key="`${month.label}-${day.date}`"
                  class="relative pb-5 last:pb-0">
                  <a
                    :href="`#${getDayAnchorId(day.date)}`"
                    class="group block w-fit transition hover:translate-x-0.5"
                    :aria-label="`Jump to ${formatTimelineDate(day.date)}`">
                    <span
                      class="absolute left-[-1.7rem] top-1.5 h-[1.125rem] w-[1.125rem] rounded-full border transition group-hover:scale-110"
                      :class="dayIndex === 0
                        ? 'border-primary-200 bg-primary-400 shadow-[0_0_0_7px_rgba(120,119,255,0.18)]'
                        : 'border-white/20 bg-[#090c12] group-hover:border-white/35'" />
                    <div class="flex flex-wrap items-center gap-2">
                      <p
                        class="text-[0.95rem] font-semibold uppercase tracking-[0.18em] transition"
                        :class="dayIndex === 0 ? 'text-white' : 'text-neutral-200 group-hover:text-white'">
                        {{ formatTimelineDate(day.date) }}
                      </p>
                      <span
                        v-if="day.version"
                        class="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-[9px] uppercase tracking-[0.35em] text-neutral-200">
                        v{{ day.version }}
                      </span>
                    </div>
                    <p class="mt-1 text-[11px] leading-5 text-neutral-300">
                      {{ day.totalEntries }} update{{ day.totalEntries === 1 ? "" : "s" }}
                    </p>
                    <p
                      v-if="day.date === latestPublishedDay"
                      class="mt-2 inline-flex items-center rounded-full border border-primary-400/30 bg-primary-500/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.35em] text-primary-100">
                      Latest
                    </p>
                  </a>
                </div>
              </div>
            </div>

            <div class="space-y-10">
              <section
                v-for="(day, dayIndex) in month.days"
                :key="`${month.label}-${day.date}`"
                :id="getDayAnchorId(day.date)"
                class="scroll-mt-28 border-t border-white/10 pt-6 animate-[changelog-rise_700ms_ease_both]"
                :style="{ animationDelay: `${monthIndex * 120 + dayIndex * 55}ms` }">
                <div class="flex items-start gap-4">
                  <span
                    class="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-primary-400 shadow-[0_0_0_6px_rgba(120,119,255,0.12)]" />
                  <div class="min-w-0">
                    <div class="flex flex-wrap items-center gap-3">
                      <p class="text-xs uppercase tracking-[0.45em] text-neutral-400">
                        {{ formatTimelineDate(day.date) }}
                      </p>
                      <span
                        v-if="day.version"
                        class="inline-flex items-center rounded-full border border-primary-400/30 bg-primary-500/15 px-2.5 py-1 text-[10px] uppercase tracking-[0.35em] text-primary-100">
                        v{{ day.version }}
                      </span>
                    </div>
                    <p class="mt-2 text-sm leading-6 text-neutral-400">
                      {{ day.totalEntries }} change{{ day.totalEntries === 1 ? "" : "s" }}
                    </p>

                    <div class="mt-5 space-y-8">
                      <section
                        v-for="(section, sectionIndex) in day.sections"
                        :key="`${month.label}-${day.date}-${section.label}`"
                        class="animate-[changelog-rise_700ms_ease_both]"
                        :style="{ animationDelay: `${monthIndex * 120 + dayIndex * 55 + sectionIndex * 30}ms` }">
                        <div class="flex items-start gap-4">
                          <span
                            class="mt-2 h-2.5 w-2.5 shrink-0 rounded-full bg-primary-400 shadow-[0_0_0_6px_rgba(120,119,255,0.12)]" />
                          <div class="min-w-0">
                            <p class="text-xs uppercase tracking-[0.45em] text-neutral-400">
                              {{ section.label }}
                            </p>

                            <div class="mt-5 space-y-6">
                              <article
                                v-for="(entry, entryIndex) in section.entries"
                                :key="`${month.label}-${day.date}-${section.label}-${entryIndex}-${entry.technicalTitle}`"
                                class="animate-[changelog-rise_700ms_ease_both]"
                                :style="{ animationDelay: `${monthIndex * 120 + dayIndex * 55 + sectionIndex * 30 + entryIndex * 22}ms` }">
                                <p class="text-sm leading-6 text-neutral-200">
                                  <span v-if="entry.userSummary">{{ entry.userSummary }}</span>
                                  <span v-else>{{ entry.technicalTitle }}</span>
                                </p>
                                <ul v-if="entry.notes.length" class="mt-3 space-y-2">
                                  <li
                                    v-for="(note, noteIndex) in entry.notes"
                                    :key="`${month.label}-${day.date}-${section.label}-${entryIndex}-${noteIndex}`"
                                    class="flex gap-3 text-sm leading-6 text-neutral-200">
                                    <span
                                      class="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-500" />
                                    <span>{{ note }}</span>
                                  </li>
                                </ul>
                              </article>
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<style scoped>
@keyframes changelog-rise {
  from {
    opacity: 0;
    transform: translateY(14px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Smooth scroll only for this page. */
:global(html) {
  scroll-behavior: smooth;
}
</style>
